import { useRef, useState } from 'react';
import { Trash2 } from 'lucide-react';
import { ApiError, errorMessage } from '../lib/api';
import { formatBRL } from '../lib/format';
import { Alert, Button, Input, Textarea } from '../ui/primitives';
import { Sheet } from '../ui/sheet';
import type { Cart } from './cart';
import { QtyStepper } from './ProductSheet';

export type SubmitPayload = {
  idempotencyKey: string;
  customerName: string | null;
  notes: string | null;
  items: { productId: string; quantity: number; optionIds: string[]; notes: string | null }[];
};

/**
 * Revisão e envio do carrinho. A chave de idempotência é criada uma vez por carrinho e reaproveitada em
 * reenvios (rede caiu, botão apertado duas vezes): o servidor devolve o mesmo pedido em vez de duplicar.
 */
export function CartSheet({
  open, onClose, cart, title = 'Seu pedido', askName = true, submitLabel = 'Enviar pedido', onSubmit, onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  cart: Cart;
  title?: string;
  askName?: boolean;
  submitLabel?: string;
  onSubmit: (payload: SubmitPayload) => Promise<{ order: { number: number } }>;
  onSuccess: (orderNumber: number) => void;
}) {
  const [name, setName] = useState(() => {
    try {
      return localStorage.getItem('garlic:name') ?? '';
    } catch {
      return '';
    }
  });
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const keyRef = useRef<string | null>(null);

  const submit = async () => {
    if (busy) return;
    setBusy(true);
    setError(undefined);
    keyRef.current ??= crypto.randomUUID();
    try {
      if (askName) {
        try {
          localStorage.setItem('garlic:name', name.trim());
        } catch {
          /* sem armazenamento: tudo bem */
        }
      }
      const r = await onSubmit({
        idempotencyKey: keyRef.current,
        customerName: askName ? name.trim() || null : null,
        notes: notes.trim() || null,
        items: cart.lines.map((l) => ({ productId: l.productId, quantity: l.quantity, optionIds: l.optionIds, notes: l.notes })),
      });
      keyRef.current = null;
      cart.clear();
      setNotes('');
      onSuccess(r.order.number);
    } catch (err) {
      // Erro de regra (produto esgotou, comanda encerrada…): o carrinho mudou de sentido, gera chave nova no próximo envio.
      if (err instanceof ApiError && err.status >= 400 && err.status < 500) keyRef.current = null;
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={<h2 className="font-display text-xl font-semibold">{title}</h2>}
      footer={
        cart.lines.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-baseline justify-between text-sm">
              <span className="text-ink-3">Total deste pedido</span>
              <span className="text-lg font-semibold tabular-nums">{formatBRL(cart.totalCents)}</span>
            </div>
            <Button variant="brand" size="lg" className="w-full" loading={busy} onClick={submit}>
              {submitLabel}
            </Button>
          </div>
        )
      }
    >
      {cart.lines.length === 0 ? (
        <p className="py-10 text-center text-sm text-ink-3">Seu carrinho está vazio.</p>
      ) : (
        <div className="space-y-5">
          {error && <Alert tone="danger">{error}</Alert>}
          <ul className="divide-y divide-line">
            {cart.lines.map((l) => (
              <li key={l.key} className="flex gap-3 py-3 first:pt-0">
                <div className="min-w-0 flex-1">
                  <p className="font-medium">{l.name}</p>
                  {l.optionLabels.length > 0 && <p className="text-xs text-ink-3">{l.optionLabels.join(' · ')}</p>}
                  {l.notes && <p className="text-xs italic text-ink-3">“{l.notes}”</p>}
                  <p className="mt-1 text-sm font-semibold tabular-nums">{formatBRL(l.unitPriceCents * l.quantity)}</p>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <QtyStepper value={l.quantity} onChange={(n) => cart.setQty(l.key, n)} max={99} />
                  <button onClick={() => cart.setQty(l.key, 0)} className="inline-flex items-center gap-1 text-xs text-ink-3 hover:text-danger-600">
                    <Trash2 className="size-3.5" /> Remover
                  </button>
                </div>
              </li>
            ))}
          </ul>
          {askName && (
            <div>
              <label htmlFor="cart-name" className="text-sm font-medium text-ink-2">Seu nome (opcional)</label>
              <Input id="cart-name" className="mt-1.5" maxLength={40} value={name} onChange={(e) => setName(e.target.value)} placeholder="Ajuda a equipe a entregar na pessoa certa" />
            </div>
          )}
          <div>
            <label htmlFor="cart-notes" className="text-sm font-medium text-ink-2">Observação geral (opcional)</label>
            <Textarea id="cart-notes" className="mt-1.5 min-h-16" maxLength={300} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          <p className="text-xs text-ink-3">Os valores são conferidos pelo restaurante no envio.</p>
        </div>
      )}
    </Sheet>
  );
}
